import { DialogComponent } from '@theme/dialog';

/**
 * <plw-quote-modal> — one quote band on the Stories listing
 * (snippets/plw-story-band.liquid).
 *
 * The band's cards clamp long submissions to a few lines, so clicking a card
 * opens the full text in the band's own fullscreen `<dialog>` (Figma
 * 3459:23359). One dialog serves every card in the band: the click handler
 * copies the clicked card's text into it, so the cards stay cheap to inject
 * (they arrive from Airtable client-side, see assets/plw-story-quotes.js).
 *
 * Extends Horizon's {@link DialogComponent} — same machinery as the video rows
 * (assets/plw-story-modal.js): top-layer + focus trap, scroll-lock, ESC close,
 * and the `dialog:open` / `dialog:close` events. The dialog lives inside the
 * band element, so it inherits the band's color scheme.
 *
 * The triggers are real `<button>`s, and the whole quotes list only exists when
 * JS runs, so there is no no-JS path to preserve here.
 *
 * @extends DialogComponent
 */
class PlwQuoteModal extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    // Delegated: cards are injected after this element connects.
    this.addEventListener('click', this.#handleTriggerClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleTriggerClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-quote-trigger]');
    if (!trigger || event.defaultPrevented) return;

    const quote = trigger.querySelector('[data-quote]')?.textContent?.trim();
    const body = this.querySelector('[data-quote-body]');
    if (!quote || !body) return;

    body.textContent = quote;
    this.showDialog();
  };
}

if (!customElements.get('plw-quote-modal')) {
  customElements.define('plw-quote-modal', PlwQuoteModal);
}
